<template>
  <div class="content mb-5">
    <div class="mb-3">
      {{ $t("Customise the user interface to fit your brand.") }}
    </div>

    <b-row>
      <b-col cols="12" md="6">
        <h4 v-if="!loading" class="mt-5 mb-3">{{ $t("Company Details") }}</h4>
        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="32px"
          width="30%"
        ></b-skeleton>

        <b-form-group
          v-if="!loading"
          label-for="title"
          class="mb-0"
          :label="this.$t('Page Title')"
          :state="$validateState($v.form.title)"
        >
          <b-form-input
            id="title"
            data-test-id="interface-company-name"
            v-model="form.title"
            @blur="setValidationInput('title', $event.target.value)"
            :state="$validateState($v.form.title)"
            :placeholder="this.$t('e.g. My Company Name')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.title.minLength || !$v.form.title.maxLength">
              {{ $t("The title must be between 2 - 25 characters.") }}
            </p>
          </template>
        </b-form-group>

        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="30%"
        ></b-skeleton>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
        <p class="mt-3 mb-6" v-if="!loading">
          * {{ $t("This will be displayed in the browser tab.") }}
        </p>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="90%"
        ></b-skeleton>

        <b-form-group
          :label="this.$t('Your Company Logo')"
          label-for="company-logo"
          v-if="!loading"
        >
          <div class="logo mt-2 mb-2">
            <img class="logo-image" :src="form.logoPreview" />
          </div>
          <p>
            *
            {{
              $t("Supports images up to X × X with the max size of 1MB.", {
                width: imageUploaderRules.maxWidth,
                height: imageUploaderRules.maxHeight,
              })
            }}
          </p>
          <b-form-file
            data-test-id="interface-logo"
            plain
            class="d-none"
            id="company-logo-input"
            ref="file-image"
            v-model="form.logo"
            :accept="imageUploaderRules.supportedTypes"
            variant="secondary"
            :state="Boolean(form.logo)"
            @change="uploadLogo"
          ></b-form-file>
          <b-button variant="outline-secondary" @click="chooseFile()">
            {{ $t("Choose a file") }}
          </b-button>
        </b-form-group>

        <b-form-group v-if="!loading">
          <h4 v-if="!loading" class="mt-5 mb-3">
            {{ $t("Reset to TitanHQ default Interface Settings") }}
          </h4>
          <b-button
            variant="danger"
            :disabled="resetting"
            @click="resetToDefaultSettings"
          >
            {{ $t("Reset to default") }}
          </b-button>
        </b-form-group>

        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="286px"
          width="100%"
        ></b-skeleton>
        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="32px"
          width="50%"
        ></b-skeleton>
        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="32px"
          width="30%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <t-save-button
      data-test-id="interface-save"
      :form-model="$v.form"
      :fetching="fetching"
      @submit="onSubmit"
    />
  </div>
</template>

<route>
{
"name": "MSP Interface Settings"
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";
import { maxLength, minLength } from "vuelidate/lib/validators";

export default {
  layout: "msp",
  methods: {
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("userInterface", [
      "updateInterface",
      "fetchInterface",
      "deleteInterfaceLogo",
      "deleteInterfaceTitle",
    ]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async resetToDefaultSettings() {
      this.resetting = true;
      this.fetching = true;
      try {
        await this.deleteInterfaceLogo();
        await this.deleteInterfaceTitle();
        await this.fetchInterface({ mspId: null });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.resetting = false;
        this.fetching = false;
        this.$router.go();
      }
    },
    chooseFile() {
      document.getElementById("company-logo-input").click();
    },
    uploadLogo(event) {
      this.fetching = true;
      try {
        const image = event.target.files[0];
        const reader = new FileReader();
        reader.readAsDataURL(image);
        reader.onload = (event) => {
          const file = this.$refs["file-image"].files[0];
          this.uploadedImage.size = file.size;
          let img = new Image();
          img.src = window.URL.createObjectURL(file);
          img.onload = () => {
            this.uploadedImage.height = img.height;
            this.uploadedImage.width = img.width;
            if (
              img.height > this.imageUploaderRules.maxHeight ||
              img.width > this.imageUploaderRules.maxWidth
            ) {
              this.displayToast({
                title: this.$t("Error"),
                message: this.$t("The image dimensions are too big."),
                duration: 2000,
                variant: "danger",
              });
              this.fetching = false;
              return;
            }
          };
          if (file.size > this.imageUploaderRules.maxSize) {
            this.displayToast({
              title: this.$t("Error"),
              message: this.$t("File size exceeds maximum of 1mb."),
              duration: 2000,
              variant: "danger",
            });
            this.fetching = false;
            return;
          }
          this.form.logo = image;
          this.form.logoPreview = event.target.result;
          this.fetching = false;
        };
        this.$v.$touch();
      } catch (err) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("Something went wrong."),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    async onSubmit() {
      this.fetching = true;
      if (
        this.uploadedImage.height > this.imageUploaderRules.maxHeight ||
        this.uploadedImage.width > this.imageUploaderRules.maxWidth
      ) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("The image dimensions are too big."),
          duration: 2000,
          variant: "danger",
        });
        this.fetching = false;
        return;
      }
      if (this.uploadedImage.size > this.imageUploaderRules.maxSize) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("File size exceeds maximum of 1mb."),
          duration: 2000,
          variant: "danger",
        });
        this.fetching = false;
        return;
      }

      try {
        const body = new FormData();
        if (this.form.logo) body.append("logo", this.form.logo);
        if (this.form.title) {
          body.append("title", this.form.title);
          document.title = this.form.title;
        }
        if (this.mspId) body.append("msp_id", this.mspId);
        await this.updateInterface({
          body,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["mspId"]),
  },
  data() {
    return {
      loading: true,
      fetching: false,
      resetting: false,
      imageUploaderRules: {
        maxSize: 1048576,
        maxWidth: 800,
        maxHeight: 200,
        supportedTypes: "image/*",
      },
      uploadedImage: {
        size: null,
        width: null,
        height: null,
      },
      form: {
        logoPreview:
          this.$userPrefs.getCurrentUi().logo ||
          require("@/assets/branding/Titan_Logo.svg"),
        logo: null,
        title: this.$userPrefs.getCurrentUi().title || null,
      },
    };
  },
  validations: {
    form: {
      title: {
        minLength: minLength(2),
        maxLength: maxLength(25),
      },
    },
  },
  async created() {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    this.setPageTitle(this.$t("Interface Settings"));
  },
};
</script>

<style lang="scss" scoped>
$dark: #dfdfdf;
$light: #e6e6e6;

.logo {
  .logo-image {
    padding: 3rem;
    width: 100%;
    background-repeat: repeat;
    background-size: 1.4rem 1.4rem;
    background-position: 0 0, 0 0, -0.7rem -0.7rem, 0.7rem 0.7rem;
    background-image: linear-gradient(45deg, $dark 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, $dark 75%),
      linear-gradient(45deg, transparent 75%, $dark 75%),
      linear-gradient(45deg, $dark 25%, $light 25%);
  }
}
</style>
